import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from 'react';

import { Auth, Hub } from 'aws-amplify';
import { HubCallback } from '@aws-amplify/core/lib/Hub';
import { CognitoUser } from '@aws-amplify/auth';
import { CognitoUserSession, ISignUpResult } from 'amazon-cognito-identity-js';

import { clientPromise } from '../http/apollo.config';
import { ME } from '../http/apollo-queries';

import Suspender from 'react-suspender';

import { IUser } from '../types/model';

import { logger } from '../helpers/logger';

interface IAuthContext {
  user: IUser | null;

  inProgress: boolean;

  signIn(username: string, password: string): Promise<CognitoUser | any>;

  signUp(
    username: string,
    password: string,
    referralCode?: string
  ): Promise<ISignUpResult>;

  signOut(): Promise<any>;

  confirmSignIn(user: CognitoUser, code: string): Promise<any>;

  refetch(): Promise<any>;

  refresh(): Promise<any>;

  confirmAccount(email: string, code: string): Promise<any>;

  resendCode(email: string): Promise<any>;

  changeEmail(email: string): Promise<any>;

  changePassword(oldPassword: string, newPassword: string): Promise<any>;

  verifyEmail(email: string, code: string): Promise<any>;

  resendVerifyEmail(email: string): Promise<any>;

  forgotPassword(email: string): Promise<any>;

  forgotPasswordSubmit(
    email: string,
    code: string,
    password: string
  ): Promise<any>;
}

interface Props {
  children?: ReactNode;
}

const signIn = (
  username: string,
  password: string
): Promise<CognitoUser | any> => {
  return Auth.signIn(username, password);
};

const signUp = (
  username: string,
  password: string,
  referralCode?: string
): Promise<ISignUpResult> => {
  return Auth.signUp(username, password);
};

const refresh = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    Auth.currentAuthenticatedUser().then((cognitoUser) => {
      const { refreshToken } = cognitoUser.getSignInUserSession();
      return cognitoUser.refreshSession(refreshToken, (err, session) => {
        if (err) {
          reject(err);
        } else {
          Hub.dispatch('auth', { event: 'refresh', data: session });
          // console.log('AuthContext:refreshSession', err, session);
          resolve(session);
        }
      });
    });
  });
};

const confirmSignIn = (user, code): Promise<any> => {
  return Auth.confirmSignIn(user, code, user.challengeName);
};

const signOut = (): Promise<any> => Auth.signOut();

const confirmAccount = (email: string, code: string) => {
  return Auth.confirmSignUp(email, code);
};

const resendCode = (email: string) => {
  return Auth.resendSignUp(email);
};

const forgotPassword = (email: string) => {
  return Auth.forgotPassword(email);
};

const forgotPasswordSubmit = (
  email: string,
  code: string,
  password: string
) => {
  return Auth.forgotPasswordSubmit(email, code, password);
};

const changeEmail = (email: string): Promise<any> => {
  return Auth.currentAuthenticatedUser().then((user) => {
    return Auth.updateUserAttributes(user, {
      email: email,
    });
  });
};

const refetch = (): Promise<any> => {
  return getUser().then((res) => {
    Hub.dispatch('auth', { event: 'refetch', data: res });
    return Promise.resolve(res);
  });
};

const changePassword = (
  oldPassword: string,
  newPassword: string
): Promise<any> => {
  return Auth.currentAuthenticatedUser().then((user) => {
    return Auth.changePassword(user, oldPassword, newPassword);
  });
};

const verifyEmail = (email: string, code: string) => {
  return Auth.verifyCurrentUserAttributeSubmit('email', code);
};

const resendVerifyEmail = (email: string) => {
  return Auth.verifyCurrentUserAttribute('email');
};

const getUser = (): Promise<any> => {
  let session;
  let cognitoUser;
  let cognitoGroups;

  return Auth.currentSession()
    .then((res: CognitoUserSession) => {
      session = res;
      cognitoGroups = session.getAccessToken().payload['cognito:groups'];
      // console.log('AuthContext:cognitoGroups', cognitoGroups);
      if (session && session.isValid()) {
        return Auth.currentUserInfo();
      }
    })
    .then((res: CognitoUser) => {
      cognitoUser = res;
      // console.log('AuthContext:cognitoUser', cognitoUser);
      if (!cognitoGroups) {
        return null;
      }

      return clientPromise.then((client) =>
        client.query({
          query: ME,
          fetchPolicy: 'network-only',
        })
      );
    })
    .then((res: any) => {
      if (!res) {
        return null;
      }

      const user: IUser = {
        id: cognitoUser.username,
        emailVerified: cognitoUser.attributes.email_verified,
        token: session.getAccessToken(),
        profile: res.data.me.profiles.items.find((p) => p.current),
        ...res.data.me,
      };
      logger.log('🔵 AuthContext:Me', user);
      return user;
    });
};

const actions = {
  signIn,
  signOut,
  confirmSignIn,
  changeEmail,
  changePassword,
  forgotPassword,
  forgotPasswordSubmit,
  signUp,
  refresh,
  refetch,
  confirmAccount,
  verifyEmail,
  resendVerifyEmail,
  resendCode,
};

const useCognito = () => {
  const [user, setUser] = useState<IUser | null>(null);
  const [inProgress, setInProgress] = useState<boolean>(true);

  const fetchUser = () => {
    getUser()
      .then((res) => {
        setUser(res);
      })
      .catch((err) => {
        logger.error('AuthContext:Err:', err);
        setUser(null);
      })
      .finally(() => {
        setInProgress(false);
      });
  };

  const authListener: HubCallback = ({ payload: { event, data } }) => {
    // logger.log('🔵 AuthContext:Hub:' + event);

    switch (event) {
      case 'signIn':
        fetchUser();
        break;

      case 'refetch':
        setUser(data);
        break;

      case 'refresh':
        break;

      case 'signOut':
        setUser(null);
        break;
    }
  };

  useEffect(() => {
    fetchUser();
    Hub.listen('auth', authListener);
    return () => Hub.remove('auth', authListener);
  }, []);

  return {
    user,
    inProgress,
    ...actions,
  };
};

const AuthContext = createContext<IAuthContext>({
  user: null,
  inProgress: true,
  ...actions,
});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }: Props) => {
  const auth = useCognito();
  // logger.log(auth.inProgress);
  return (
    <AuthContext.Provider value={auth}>
      <Suspender suspend={auth.inProgress}>{children}</Suspender>
    </AuthContext.Provider>
  );
};

export const getToken = async (): Promise<string | null> => {
  try {
    const session = await Auth.currentSession();
    return session.getAccessToken().getJwtToken();
  } catch (e) {
    return null;
  }
};
